import React from 'react'
import { Chip as MuiChip, type ChipProps as MuiChipProps, Skeleton } from '@mui/material'

import { type AccentColorName, accentColors, lightTokens } from '~tokens/internal'
import { type LicenseStatus } from '~types'
import { addOpacity, ensureHash, getCustomColorChipStyle } from '~utils/colorUtils'

import './Chip.scss'
import Icon, { type SemanticIconName } from './Icon'

export type ChipColor = AccentColorName

export interface ChipProps extends Omit<MuiChipProps, 'color'> {
  color?: ChipColor | string
  loading?: boolean
  hasBorder?: boolean
  visualVariant?: 'default' | 'inverted'
}

// TODO: remove custom color option when tags are switched to accent colors
const Chip = React.forwardRef<HTMLDivElement, ChipProps>(function Chip(
  {
    color = 'neutral',
    icon,
    sx,
    loading,
    label,
    hasBorder = false,
    visualVariant = 'default',
    className,
    ...props
  },
  ref,
) {
  const isAccentColor = color in accentColors
  const palette = isAccentColor ? accentColors[color as AccentColorName] : accentColors['neutral']
  const isTransparent = color === 'transparent'
  const isInverted = visualVariant === 'inverted'
  const customColor =
    !isAccentColor && !isTransparent && typeof color === 'string' ? ensureHash(color) : color
  const customChipStyle =
    !isAccentColor && !isTransparent ? getCustomColorChipStyle(customColor) : undefined
  const customTextColor = customChipStyle?.text

  // Clone icon to inject fontSize="inherit" so it inherits from theme's .MuiChip-icon sizing
  const clonedIcon =
    icon && React.isValidElement(icon)
      ? React.cloneElement(icon as React.ReactElement<{ fontSize?: string }>, {
          fontSize: 'inherit',
        })
      : icon
  const renderedLabel = loading ? (
    <span className='internal-chip-loading'>
      {label !== undefined && label !== null && label !== '' && (
        <span className='internal-chip-loading__label'>{label}</span>
      )}
      <Skeleton variant='rounded' className='internal-chip-loading__skeleton' />
    </span>
  ) : (
    label
  )
  return (
    <MuiChip
      {...props}
      ref={ref}
      className={className}
      label={renderedLabel}
      icon={clonedIcon}
      sx={{
        backgroundColor: isInverted
          ? isAccentColor
            ? palette.dark
            : addOpacity(customColor, 0.85)
          : isAccentColor
            ? color === 'neutral'
              ? palette.light
              : palette.lighter
            : isTransparent
              ? 'transparent'
              : customChipStyle?.background,
        color: isInverted
          ? lightTokens.common.whiteStates.main
          : isAccentColor
            ? palette.dark
            : isTransparent
              ? 'inherit'
              : customTextColor,
        '& .MuiChip-deleteIcon': {
          color: isInverted
            ? lightTokens.common.whiteStates.main
            : isAccentColor
              ? palette.dark
              : isTransparent
                ? 'inherit'
                : customTextColor,
          '&:hover': {
            color: isInverted
              ? lightTokens.common.whiteStates.main
              : isAccentColor
                ? palette.darker
                : isTransparent
                  ? 'inherit'
                  : customTextColor,
            opacity: isAccentColor || isTransparent ? 1 : 0.7,
          },
        },
        ...(hasBorder &&
          !isInverted && {
            border: `1px solid ${palette.mediumLight}`,
          }),
        '& .MuiChip-icon': {
          color: isInverted
            ? lightTokens.common.whiteStates.main
            : isAccentColor
              ? palette.dark
              : isTransparent
                ? 'inherit'
                : customTextColor,
        },
        ...(sx as Record<string, unknown>),
      }}
    />
  )
})

export default Chip

// Known Chip Presets
export type KnownChipType = 'closed' | 'open' | 'live' | 'draft' | 'archived'

const knownChipConfig: Record<
  KnownChipType,
  {
    label: string
    color: ChipColor
    icon: React.ReactElement
  }
> = {
  closed: {
    label: 'Closed',
    color: 'green',
    icon: <Icon name='success' />,
  },
  open: {
    label: 'Open',
    color: 'indigo',
    icon: <Icon name='circleCircle' />,
  },
  live: {
    label: 'Live',
    color: 'green',
    icon: <Icon name='sensors' />,
  },
  draft: {
    label: 'Draft',
    color: 'neutral',
    icon: <Icon name='editNote' />,
  },
  archived: {
    label: 'Archived',
    color: 'neutral',
    icon: <Icon name='archive' />,
  },
}

interface KnownChipProps extends Omit<ChipProps, 'color' | 'label' | 'icon'> {
  type: KnownChipType
}

export const KnownChip: React.FC<KnownChipProps> = ({ type, sx, ...props }) => {
  const config = knownChipConfig[type]
  return <Chip label={config.label} color={config.color} icon={config.icon} sx={sx} {...props} />
}

// Status Chip Variant
export type StatusChipType = 'open' | 'closed' | 'deleted' | 'hidden' | 'unsubmitted'

const statusExplanation: Record<StatusChipType, string> = {
  unsubmitted:
    'Removes from most case dashboard views, typically represents a case a resident has started but has not yet provided sufficient information.',
  open: 'Appears in most case dashboard views and makes all step assignments active.',
  closed:
    'Deactivates step assignments and removes case from active case dashboard views (e.g. My To Do), but keeps it reflected in your data on the process.',
  deleted:
    'Soft delete that removes case from most case dashboard views and excludes it from data on the process.',
  hidden: 'Removes case from most case dashboard views and excludes it from data on the process.',
}

export interface StatusChipConfig {
  label: string
  color: ChipColor
  icon: SemanticIconName
  fill?: 0 | 1
  explanation?: string
}

export const caseStatusConfig: Record<StatusChipType, StatusChipConfig> = {
  open: {
    label: 'Open',
    color: 'blue',
    icon: 'radioUnchecked',
    explanation: statusExplanation.open,
  },
  closed: {
    label: 'Closed',
    color: 'purple',
    icon: 'radioChecked',
    explanation: statusExplanation.closed,
  },
  deleted: {
    label: 'Archived',
    color: 'neutral',
    icon: 'archive',
    explanation: statusExplanation.deleted,
  },
  hidden: {
    label: 'Hidden',
    color: 'neutral',
    icon: 'visibilityOff',
    explanation: statusExplanation.hidden,
  },
  unsubmitted: {
    label: 'Unsubmitted',
    color: 'neutral',
    icon: 'dataUsage',
    explanation: statusExplanation.unsubmitted,
  },
}

interface StatusChipProps extends Omit<ChipProps, 'color' | 'label' | 'icon'> {
  config: StatusChipConfig
}

export const StatusChip: React.FC<StatusChipProps> = ({ config, style, ...props }) => {
  const palette = accentColors[config.color]
  const clonedIcon = React.cloneElement(
    (<Icon name={config.icon} fill={config.fill} />) as React.ReactElement<{ fontSize?: string }>,
    { fontSize: 'inherit' },
  )
  return (
    <MuiChip
      {...props}
      className={`status-chip${config.label === 'Unsubmitted' ? ' status-chip--border' : ''}`}
      style={
        {
          '--status-chip-bg':
            config.label === 'Unsubmitted'
              ? ''
              : config.label === 'Archived'
                ? palette.darker
                : palette.main,
          '--status-chip-color':
            config.label === 'Unsubmitted'
              ? lightTokens.neutral.main
              : lightTokens.common.whiteStates.main,
          ...style,
        } as React.CSSProperties
      }
      icon={clonedIcon}
      title={config.explanation}
      label={config.label}
    />
  )
}

// License Status Chip
export const licenseStatusConfig: Record<
  LicenseStatus,
  { label: string; color: ChipColor; icon: SemanticIconName; fill?: 0 | 1; markerColor: string }
> = {
  active: {
    label: 'Active',
    color: 'green',
    icon: 'verified',
    markerColor: accentColors.green.main,
  },
  suspended: {
    label: 'Suspended',
    color: 'orange',
    icon: 'pauseCircle',
    markerColor: accentColors.orange.main,
  },
  revoked: {
    label: 'Revoked',
    color: 'red',
    icon: 'block',
    fill: 0,
    markerColor: accentColors.red.main,
  },
  expired: {
    label: 'Expired',
    color: 'neutral',
    icon: 'eventBusy',
    markerColor: accentColors.neutral.main,
  },
  deleted: {
    label: 'Deleted',
    color: 'neutral',
    icon: 'delete',
    markerColor: accentColors.neutral.darker,
  },
}

interface LicenseStatusChipProps extends Omit<ChipProps, 'color' | 'label' | 'icon'> {
  status: LicenseStatus
}

export const LicenseStatusChip: React.FC<LicenseStatusChipProps> = ({
  status,
  style,
  ...props
}) => {
  const config = licenseStatusConfig[status]
  const palette = accentColors[config.color]
  const icon = React.cloneElement(
    (<Icon name={config.icon} fill={config.fill} />) as React.ReactElement<{ fontSize?: string }>,
    { fontSize: 'inherit' },
  )
  return (
    <MuiChip
      {...props}
      className={'status-chip'}
      style={
        {
          '--status-chip-bg': palette.dark,
          '--status-chip-color': palette.lighter,
          ...style,
        } as React.CSSProperties
      }
      icon={icon}
      label={config.label}
    />
  )
}
